/**
 * The web implementation of `@/storage/mmkv`, wired by a Vite alias.
 *
 * MMKV and localStorage are both SYNCHRONOUS string key/value stores, which is the
 * property that matters: amazon-cognito-identity-js requires a synchronous Storage, and
 * given an async one it silently falls back to in-memory and logs the user out on every
 * reload. Values are stored with a one-character type tag so numbers and booleans come
 * back as numbers and booleans, matching MMKV's typed getters.
 *
 * Every access is wrapped: a browser in private mode or with site data blocked throws
 * from localStorage itself, and losing a stored preference must never take down the app.
 */
type Tag = 's' | 'n' | 'b';

function readRaw(key: string): string | undefined {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? undefined : v;
  } catch {
    return undefined;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota or blocked storage — a lost preference is survivable, a crash is not */
  }
}

function read(key: string, tag: Tag): string | undefined {
  const raw = readRaw(key);
  if (raw === undefined) return undefined;
  // Untagged values were written by a third party (Cognito writes through
  // cognitoStorage.setItem -> mmkv.setString) and are plain strings.
  if (raw.length >= 2 && raw[1] === ':' && (raw[0] === 's' || raw[0] === 'n' || raw[0] === 'b')) {
    return raw[0] === tag ? raw.slice(2) : undefined;
  }
  return tag === 's' ? raw : undefined;
}

export const storage = {
  getString: (key: string): string | undefined => read(key, 's'),
  set: (key: string, value: string | number | boolean): void => {
    const tag: Tag = typeof value === 'number' ? 'n' : typeof value === 'boolean' ? 'b' : 's';
    writeRaw(key, `${tag}:${String(value)}`);
  },
  getNumber: (key: string): number | undefined => {
    const v = read(key, 'n');
    if (v === undefined) return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  },
  getBoolean: (key: string): boolean | undefined => {
    const v = read(key, 'b');
    return v === undefined ? undefined : v === 'true';
  },
  remove: (key: string): void => {
    try { window.localStorage.removeItem(key); } catch { /* blocked storage */ }
  },
  contains: (key: string): boolean => readRaw(key) !== undefined,
  clearAll: (): void => {
    try { window.localStorage.clear(); } catch { /* blocked storage */ }
  },
  getAllKeys: (): string[] => {
    try {
      const keys: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k !== null) keys.push(k);
      }
      return keys;
    } catch {
      return [];
    }
  },
};

export const mmkv = {
  getString: (key: string): string | undefined => storage.getString(key),
  setString: (key: string, value: string): void => storage.set(key, value),
  getNumber: (key: string): number | undefined => storage.getNumber(key),
  setNumber: (key: string, value: number): void => storage.set(key, value),
  getBoolean: (key: string): boolean | undefined => storage.getBoolean(key),
  setBoolean: (key: string, value: boolean): void => storage.set(key, value),
  getObject: <T>(key: string): T | undefined => {
    const str = storage.getString(key);
    if (str === undefined) return undefined;
    try {
      return JSON.parse(str) as T;
    } catch {
      return undefined;
    }
  },
  setObject: (key: string, value: unknown): void => storage.set(key, JSON.stringify(value)),
  delete: (key: string): boolean => {
    const had = storage.contains(key);
    storage.remove(key);
    return had;
  },
  contains: (key: string): boolean => storage.contains(key),
  clearAll: (): void => storage.clearAll(),
};
